import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { FileText, Clock, CheckCircle, Calendar } from 'lucide-react';
import { useAuth } from '@contexts/AuthContext';
import { formatGivenName } from '@utils/stringUtils';
import { assessmentService, schedulerService } from '@services';
import PageLayout from '@components/layout/PageLayout';
import { RecentActivity } from '@components/features/dashboard';
import Button from '@components/common/Button';
import DashboardHeader from '@components/common/DashboardHeader';
import DashboardGrid from '@components/common/DashboardGrid';
import DashboardRow from '@components/common/DashboardRow';
import DashboardContainer from '@components/common/DashboardContainer';
import DashboardCard from '@components/common/DashboardCard';
import DashboardCol from '@components/common/DashboardCol';
import MetricsGrid from '@components/common/MetricsGrid';
import MetricCard from '@components/common/MetricCard';
import Card from '@components/common/Card';
import ErrorState from '@components/common/ErrorState';
import styles from './AssessorDashboardPage.module.css';

const AssessorDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Fetch assessor's assigned assessments
  const {
    data: assessmentsData,
    isLoading,
    error
  } = useQuery({
    queryKey: ['assessments', 'assessor', user?.id],
    queryFn: () =>
      assessmentService.getAssessments({
        assessor_id: user?.id,
        limit: 1000,
        sortBy: 'updated_at',
        order: 'DESC'
      }),
    refetchInterval: 60000, // Refresh every minute
    enabled: !!user?.id // Only run query when user is loaded
  });

  // Fetch assessor's scheduled meetings
  const { data: meetingsData } = useQuery({
    queryKey: ['assessor-meetings-scheduled', user?.id],
    queryFn: () =>
      schedulerService.getScheduleItems({
        state: 'scheduled',
        processedByMe: true,
        limit: 100,
        offset: 0,
        viewMode: 'assessor' as const
      }),
    refetchInterval: 60000,
    enabled: !!user?.id
  });

  // For assessors, all returned assessments are already filtered by the backend
  const assignedAssessments = React.useMemo(() => {
    if (!assessmentsData) return [];
    return assessmentsData.data;
  }, [assessmentsData]);

  // Calculate assessor-specific metrics
  const assessorMetrics = React.useMemo(() => {
    const total = assignedAssessments.length;
    const completed = assignedAssessments.filter((a) => a.assessment_status === 'Final').length;
    const pending = assignedAssessments.filter((a) => a.assessment_status === 'Pending').length;
    const scheduledMeetings = meetingsData?.total || 0;

    return {
      total,
      completed,
      pending,
      scheduledMeetings,
      completionRate: total > 0 ? Math.round((completed / total) * 100) : 0
    };
  }, [assignedAssessments, meetingsData]);

  if (error) {
    return (
      <PageLayout>
        <ErrorState
          title="Dashboard Unavailable"
          message="Unable to load your assessment data. Please try refreshing the page."
          action={
            <Button variant="primary" onClick={() => window.location.reload()}>
              Refresh Page
            </Button>
          }
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <DashboardContainer data-test-id="assessor-dashboard">
        {/* Welcome Header */}
        <DashboardHeader
          title={`Welcome back, ${user ? formatGivenName(user) : ''}!`}
          subtitle="Here are your assigned assessments and current workload."
          role="Assessor Dashboard"
          lastUpdated={`Last updated: ${new Date().toLocaleTimeString()}`}
        />

        {/* Main Dashboard Grid */}
        <DashboardGrid>
          {/* Compact Assessor Metrics */}
          <DashboardRow>
            <MetricsGrid>
              <MetricCard
                icon={<FileText size={24} />}
                label="Total Assigned"
                value={assessorMetrics.total}
                subtitle="Assessments under your review"
                variant="blue"
              />

              <MetricCard
                icon={<Clock size={24} />}
                label="Pending"
                value={assessorMetrics.pending}
                subtitle="Awaiting assessment"
                variant="orange"
              />

              <MetricCard
                icon={<CheckCircle size={24} />}
                label="Final"
                value={assessorMetrics.completed}
                subtitle={`${assessorMetrics.completionRate}% completion rate`}
                variant="green"
              />

              <MetricCard
                icon={<Calendar size={24} />}
                label="Scheduled"
                value={assessorMetrics.scheduledMeetings}
                subtitle="Upcoming meetings"
                variant="purple"
              />
            </MetricsGrid>
          </DashboardRow>

          {/* Enhanced Quick Actions */}
          <DashboardRow variant="2col">
            <DashboardCol>
              <Card variant="primary" className={styles.dashboardCard}>
                <Card.Header>
                  <h3>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12,6 12,12 16,14" />
                    </svg>
                    Active Work
                  </h3>
                </Card.Header>
                <Card.Content>
                  <Card.QuickActionsGrid>
                    <Card.QuickActionButton onClick={() => navigate('/assessor/meetings')}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                      <Card.QuickActionText>
                        <span>Scheduled Meetings</span>
                        <small>{assessorMetrics.scheduledMeetings} upcoming</small>
                      </Card.QuickActionText>
                    </Card.QuickActionButton>

                    <Card.QuickActionButton onClick={() => navigate('/evidence-review')}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <path d="m9 12 2 2 4-4" />
                      </svg>
                      <Card.QuickActionText>
                        <span>Review Evidence</span>
                        <small>Pending evidence submissions</small>
                      </Card.QuickActionText>
                    </Card.QuickActionButton>
                  </Card.QuickActionsGrid>
                </Card.Content>
              </Card>
            </DashboardCol>

            <DashboardCol>
              <Card variant="secondary" className={styles.dashboardCard}>
                <Card.Header>
                  <h3>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                    Scheduled & Planning
                  </h3>
                </Card.Header>
                <Card.Content>
                  <Card.QuickActionsGrid>
                    <Card.QuickActionButton onClick={() => navigate('/assessments?status=Pending')}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12,6 12,12 16,14" />
                      </svg>
                      <Card.QuickActionText>
                        <span>Start Evaluations</span>
                        <small>{assessorMetrics.pending} pending</small>
                      </Card.QuickActionText>
                    </Card.QuickActionButton>

                    <Card.QuickActionButton onClick={() => navigate('/assessments')}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14,2 L6,2 C4.895,2 4,2.895 4,4 L4,20 C4,21.105 4.895,22 6,22 L18,22 C19.105,22 20,21.105 20,20 L20,8 L14,2 Z" />
                        <path d="M14,2 L14,8 L20,8" />
                        <line x1="16" y1="13" x2="8" y2="13" />
                        <line x1="16" y1="17" x2="8" y2="17" />
                        <line x1="10" y1="9" x2="8" y2="9" />
                      </svg>
                      <Card.QuickActionText>
                        <span>All Assignments</span>
                        <small>{assessorMetrics.total} total assessments</small>
                      </Card.QuickActionText>
                    </Card.QuickActionButton>
                  </Card.QuickActionsGrid>
                </Card.Content>
              </Card>
            </DashboardCol>
          </DashboardRow>

          {/* Recent Assigned Assessments */}
          <DashboardCard>
            <RecentActivity assessments={assignedAssessments.slice(0, 10)} isLoading={isLoading} />
          </DashboardCard>

        </DashboardGrid>
      </DashboardContainer>
    </PageLayout>
  );
};

export default AssessorDashboardPage;
